<template>
    <div v-if="routine && currentExercise" class="hist-block">
        <div class="hist-hdr">
            <span class="hist-hdr-lbl">{{ $t('common.exercise_history') }}</span>
            <div class="hist-nav">
                <button type="button" class="hist-nav-btn" @click.stop="prevBlock">
                    <v-icon size="12">mdi-chevron-left</v-icon>
                </button>
                <span class="hist-nav-lbl">{{ blockName }}</span>
                <button type="button" class="hist-nav-btn" @click.stop="nextBlock">
                    <v-icon size="12">mdi-chevron-right</v-icon>
                </button>
            </div>
        </div>
        <div class="hist-ex">
            <div class="hist-ex-main" @click="onClickExercise(currentExercise)">
                <div class="hist-ex-thumb">
                    <img v-if="thumb" :src="thumb" alt="">
                    <v-icon v-else size="18" color="txtMuted">mdi-image-outline</v-icon>
                </div>
                <div class="hist-ex-info">
                    <div class="hist-ex-name">{{ currentExercise.name }}</div>
                    <div v-if="exerciseMeta" class="hist-ex-meta">{{ exerciseMeta }}</div>
                </div>
            </div>
            <div v-if="blockExercises.length > 1" class="hist-ex-nav">
                <button type="button" class="hist-ex-arrow" @click.stop="prevExercise">
                    <v-icon size="12">mdi-chevron-left</v-icon>
                </button>
                <span class="hist-ex-pos">{{ currentExerciseIndex + 1 }}/{{ blockExercises.length }}</span>
                <button type="button" class="hist-ex-arrow" @click.stop="nextExercise">
                    <v-icon size="12">mdi-chevron-right</v-icon>
                </button>
            </div>
        </div>
        <exercise-history-dialog :collection="collection" :routine="routine" ref="history" />
    </div>
</template>

<script>
import ExerciseHistoryDialog from './ExerciseHistoryDialog.vue';
import { exerciseSetsLabel } from '../../../utils/Exercises';
export default {
    components: { ExerciseHistoryDialog },
    name: "ExercisesCarouselHistory",
    props: {
        routine: {
            type: Object,
            default: null
        },
        collection: {
            type: String,
            default: null,
        },
        customTitleStyle: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentBlockIndex: 0,
            currentExerciseIndex: 0,
        }
    },
    computed: {
        blocks(vm = this) {
            return vm.routine?.blocks || []
        },
        currentBlock(vm = this) {
            if (!vm.blocks.length) return null
            return vm.blocks[Math.min(vm.currentBlockIndex, vm.blocks.length - 1)]
        },
        blockExercises(vm = this) {
            return vm.currentBlock?.exercises || []
        },
        currentExercise(vm = this) {
            if (!vm.blockExercises.length) return null
            return vm.blockExercises[Math.min(vm.currentExerciseIndex, vm.blockExercises.length - 1)]
        },
        blockName(vm = this) {
            return vm.blockNameLabel(vm.currentBlock?.name) || `${vm.$tc('common.blocks', 1)} ${vm.currentBlockIndex + 1}`
        },
        exerciseMeta(vm = this) {
            return vm.currentExercise ? exerciseSetsLabel(vm.currentExercise, vm.currentBlock) : ''
        },
        thumb(vm = this) {
            return vm.currentExercise?.img || vm.currentExercise?.video?.thumb || null
        }
    },
    methods: {
        onClickExercise(exercise) {
            const vm = this;
            vm.$refs.history.show(exercise)
        },
        prevBlock() {
            if (!this.blocks.length) return
            this.currentBlockIndex = this.currentBlockIndex === 0 ? this.blocks.length - 1 : this.currentBlockIndex - 1
            this.currentExerciseIndex = 0
        },
        nextBlock() {
            if (!this.blocks.length) return
            this.currentBlockIndex = this.currentBlockIndex === this.blocks.length - 1 ? 0 : this.currentBlockIndex + 1
            this.currentExerciseIndex = 0
        },
        prevExercise() {
            if (!this.blockExercises.length) return
            this.currentExerciseIndex = this.currentExerciseIndex === 0 ? this.blockExercises.length - 1 : this.currentExerciseIndex - 1
        },
        nextExercise() {
            if (!this.blockExercises.length) return
            this.currentExerciseIndex = this.currentExerciseIndex === this.blockExercises.length - 1 ? 0 : this.currentExerciseIndex + 1
        }
    },
    watch: {
        routine() {
            this.currentBlockIndex = 0
            this.currentExerciseIndex = 0
        }
    },
}
</script>

<style scoped>
.hist-block {
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
}

.hist-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 10px;
    background: var(--v-surface2-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.hist-hdr-lbl {
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: var(--v-txtMuted-base);
}

.hist-nav {
    display: flex;
    align-items: center;
    gap: 6px;
}

.hist-nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 5px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    transition: border-color .15s, color .15s;
}

.hist-nav-btn:hover {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.hist-nav-lbl {
    min-width: 56px;
    text-align: center;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.hist-ex {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: var(--v-surface-base);
}

.hist-ex-main {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    cursor: pointer;
}

.hist-ex-main:hover .hist-ex-name {
    color: var(--v-accent-base);
}

.hist-ex-nav {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

.hist-ex-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 5px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    transition: border-color .15s, color .15s;
}

.hist-ex-arrow:hover {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.hist-ex-pos {
    min-width: 30px;
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
}

.hist-ex-thumb {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 7px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    overflow: hidden;
    flex-shrink: 0;
}

.hist-ex-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.hist-ex-info {
    flex: 1;
    min-width: 0;
}

.hist-ex-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.hist-ex-meta {
    margin-top: 2px;
    font-size: 10px;
    color: var(--v-txtMuted-base);
}
</style>